/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useCallback, useMemo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconDownloadOutline18 as Download,
} from "nucleo-ui-outline-18"
import { Controlled as ControlledZoom } from "react-medium-image-zoom"

import { cn } from "@productlane/lib/src/cn"

import type { FormattedAttachment } from "../../hooks/useAttachments"
import type { FileCategory } from "../../hooks/useFileIcon"
import { useFileIcon } from "../../hooks/useFileIcon"
import { Tooltip } from "../tooltip"
import { VideoPlayer } from "../video-player"

export type AttachmentItemVariant = "default" | "inverted"

const CATEGORY_ICON_COLOR: Record<FileCategory, string> = {
	image: "text-green-600 dark:text-green-400",
	video: "text-purple-500 dark:text-purple-400",
	document: "text-red-500 dark:text-red-400",
	text: "text-blue-500 dark:text-blue-400",
	code: "text-purple-500 dark:text-purple-400",
	table: "text-green-600 dark:text-green-400",
	audio: "text-amber-500 dark:text-amber-400",
	json: "text-amber-500 dark:text-amber-400",
	archive: "text-yellow-500 dark:text-yellow-400",
	email: "text-blue-500 dark:text-blue-400",
	calendar: "text-red-500 dark:text-red-400",
	other: "text-gray-500 dark:text-gray-300",
}

// Badge text sits on a white pill in both themes, so use a single shade with enough contrast on white.
const CATEGORY_BADGE_COLOR: Record<FileCategory, string> = {
	image: "text-green-600",
	video: "text-purple-600",
	document: "text-red-600",
	text: "text-blue-600",
	code: "text-purple-600",
	table: "text-green-600",
	audio: "text-amber-600",
	json: "text-amber-600",
	archive: "text-yellow-600",
	email: "text-blue-600",
	calendar: "text-red-600",
	other: "text-gray-600",
}

export function AttachmentItem({
	attachment,
	variant = "default",
	showTooltip = true,
	onImageClick,
	enableViewer = false,
	showPointer = true,
}: {
	attachment: FormattedAttachment
	variant?: AttachmentItemVariant
	showTooltip?: boolean
	onImageClick?: (originRect: DOMRect) => void
	enableViewer?: boolean
	showPointer?: boolean
}) {
	const { ContentType: type, Name: name, Content: url } = attachment
	const isDataUrl = url?.startsWith("data:") || !url.includes("http")
	const convertedUrl = useMemo(() => {
		if (url?.includes("http") || url?.startsWith("data:")) {
			return url
		}

		return `data:${type};base64,${url}`
	}, [url, type])

	const [isZoomed, setIsZoomed] = useState(false)
	const [hadDisabledPointerEvents, setHadDisabledPointerEvents] =
		useState(false)
	const [justDownloaded, setJustDownloaded] = useState(false)

	const handleZoomChange = useCallback(
		(shouldZoom: boolean) => {
			if (shouldZoom) {
				const pointerEventsDisabled =
					document.body.style.pointerEvents === "none"
				if (pointerEventsDisabled) {
					setHadDisabledPointerEvents(true)
					document.body.style.pointerEvents = "auto"
				}
				setIsZoomed(true)
			} else {
				if (hadDisabledPointerEvents) {
					setHadDisabledPointerEvents(false)
					document.body.style.pointerEvents = "none"
				}
				setIsZoomed(false)
			}
		},
		[hadDisabledPointerEvents],
	)

	const handleDownloadFile = async () => {
		if (!convertedUrl) return

		try {
			const response = await fetch(convertedUrl)
			if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`)

			const blob = await response.blob()
			const blobUrl = URL.createObjectURL(blob)

			const link = document.createElement("a")
			link.href = blobUrl
			link.download = attachment.Name
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			URL.revokeObjectURL(blobUrl)
		} catch (err) {
			console.error("Download failed:", err)
			handleOpenInNewWindow()
		}
	}

	const handleOpenInNewWindow = () => {
		if (!convertedUrl) {
			return
		}
		window.open(convertedUrl, "_blank", "noopener,noreferrer")
	}

	const {
		isImageFile,
		isVideoFile,
		icon: fileIcon,
		category,
	} = useFileIcon(type, name, "size-5")

	const iconClassName =
		variant === "inverted"
			? "text-white dark:text-black"
			: CATEGORY_ICON_COLOR[category]

	const typeLabel = (
		name.includes(".") ? (name.split(".").pop() ?? "") : category
	).toUpperCase()

	const titleText = name.includes(".")
		? name.slice(0, name.lastIndexOf("."))
		: name

	const content = useMemo(() => {
		if (isImageFile) {
			if (enableViewer) {
				return (
					<img
						alt={name}
						className="size-20 cursor-zoom-in border object-cover"
						decoding="async"
						height={80}
						loading="lazy"
						src={convertedUrl ?? ""}
						width={80}
					/>
				)
			}
			return (
				<ControlledZoom isZoomed={isZoomed} onZoomChange={handleZoomChange}>
					<img
						alt={name}
						className="size-20 border object-cover"
						decoding="async"
						height={80}
						loading="lazy"
						src={convertedUrl ?? ""}
						width={80}
					/>
				</ControlledZoom>
			)
		}
		if (isVideoFile) {
			if (isDataUrl) {
				return (
					<div
						className={cn(
							"flex size-16 items-center justify-center",
							iconClassName,
						)}
					>
						{fileIcon}
					</div>
				)
			} else {
				return <VideoPlayer src={convertedUrl} className="h-36 md:h-48" />
			}
		}
		return (
			<div className="relative flex h-full w-full flex-col justify-between p-3">
				<span
					className={cn(
						"line-clamp-2 break-all text-left text-[13px] leading-snug",
						variant === "inverted" ? "text-white/90" : "text-secondary",
					)}
				>
					{titleText}
				</span>
				<span
					className={cn(
						"w-fit rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold tracking-wide shadow-sm ring-1 ring-black/5",
						CATEGORY_BADGE_COLOR[category],
					)}
				>
					{typeLabel}
				</span>
				<span
					className={cn(
						"pointer-events-none absolute bottom-3 right-3 opacity-0 transition-opacity duration-150 ease-out group-hover/file:opacity-100",
						variant === "inverted" ? "text-white/80" : "text-tertiary",
						{ "opacity-100": justDownloaded },
					)}
				>
					{justDownloaded ? (
						<Check className="size-4 duration-200 animate-in zoom-in-50" />
					) : (
						<Download className="size-4" />
					)}
				</span>
			</div>
		)
	}, [
		isImageFile,
		isVideoFile,
		fileIcon,
		name,
		convertedUrl,
		isDataUrl,
		iconClassName,
		category,
		variant,
		typeLabel,
		titleText,
		enableViewer,
		isZoomed,
		handleZoomChange,
		justDownloaded,
	])

	return (
		<Tooltip label={name} enabled={showTooltip && (isImageFile || isVideoFile)}>
			<div
				className={cn(
					"m-px flex",
					isVideoFile
						? "h-fit max-h-48 w-fit items-center justify-center rounded"
						: isImageFile
							? "h-fit max-h-20 w-fit items-center justify-center overflow-hidden rounded"
							: "h-[100px] w-40 overflow-hidden rounded-lg",
					{
						"bg-gray-150 ring-1 ring-gray-900/15 dark:bg-gray-800 dark:ring-white/10":
							!(isImageFile || isVideoFile) && variant === "default",
						"ring-1 ring-gray-50/15 dark:ring-gray-900/20":
							!(isImageFile || isVideoFile) && variant === "inverted",
						"group/file transition-colors duration-100 ease-out hover:bg-gray-900/5 dark:hover:bg-gray-700":
							!(isImageFile || isVideoFile),
						"cursor-pointer": !(isImageFile || isVideoFile) && showPointer,
					},
				)}
				onClick={async (e) => {
					if (isImageFile) {
						if (!enableViewer) return
						const img = (e.currentTarget as HTMLElement).querySelector("img")
						if (img) onImageClick?.(img.getBoundingClientRect())
						return
					}
					if (isVideoFile) {
						if (isDataUrl) handleOpenInNewWindow()
						return
					}

					setJustDownloaded(true)
					setTimeout(() => setJustDownloaded(false), 1200)
					await handleDownloadFile()
				}}
			>
				{content}
			</div>
		</Tooltip>
	)
}
